import { useState, useEffect, useCallback } from "react";
import { useLocation, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { getCasinoGameDetails, getMatchExposure } from "@/services/casinoApi";
import BetTableRace20 from "../components/race20/BetTableRace20";
import ResultRace20 from "../components/race20/ResultRace20";
import RightSection from "../components/RightSection/RightSection";
import styles from "./Race20.module.css";
import VideoBox from "../components/common/VideoBox";
import CasinoHeading from "../components/common/CasinoHeading";

function extractCasinoGame(url) {
  const match = url.match(/\/lord-games\/([^/?#]+)/);
  return match ? match[1] : null;
}

// Helper function to normalize string for matching
const normalizeString = (str) => {
  if (!str) return "";
  return str.replace(/\s+/g, ' ').trim().toLowerCase();
};

// Helper function to create all exposure key variations
const createExposureKeys = (selection) => {
  if (!selection) return [];
  const keys = [
    selection,
    selection.toLowerCase(),
    selection.toLowerCase().trim(),
    selection.trim(),
    normalizeString(selection),
  ];
  return [...new Set(keys.filter(k => k))];
};

export default function Race20() {
  const [gameData, setGameData] = useState({});
  const [activeTab, setActiveTab] = useState("game");
  const [iframeSrc, setIframeSrc] = useState("");
  const { pathname } = useLocation();
  const [subData, setSubData] = useState([]);
  const [data1, setData1] = useState([]);
  const [Playerscore, setPlayerscore] = useState({});
  const [exposures, setExposures] = useState({});
  const [myBets, setMyBets] = useState([]);

  useEffect(() => {
    const name = extractCasinoGame(pathname) || "race20";
    setIframeSrc(`https://casino-stream.softgamingapi.com/casino-tv?id=${name}`);

    const fetchGameData = async () => {
      try {
        const response = await getCasinoGameDetails(name);
        setGameData(response);
      } catch (err) {
        console.error("Error fetching game data:", err);
      }
    };

    // Start API calls after 5 seconds delay
    let intervalId = null;
    const initialTimeout = setTimeout(() => {
      fetchGameData();
      intervalId = setInterval(fetchGameData, 1500);
    }, 5000);

    return () => {
      clearTimeout(initialTimeout);
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [pathname]);

  useEffect(() => {
    if (gameData) {
      const subData = gameData?.data?.data?.sub || gameData?.data?.sub || [];
      setSubData(subData);
      setData1(subData.slice(0, 12));

      if (subData.length > 11) {
        // Map data correctly: sid 1-4 are K cards, sid 5-6 are Total points/cards, sid 7-12 are Win with X
        const playerAScore = {
          // K of spade (sid 1, index 0)
          ksb: subData[0]?.b || 0,
          ksbs: subData[0]?.bs || 0,
          ksl: subData[0]?.l || 0,
          ksls: subData[0]?.ls || 0,
          // K of heart (sid 2, index 1)
          khb: subData[1]?.b || 0,
          khbs: subData[1]?.bs || 0,
          khl: subData[1]?.l || 0,
          khls: subData[1]?.ls || 0,
          // K of club (sid 3, index 2)
          kcb: subData[2]?.b || 0,
          kcbs: subData[2]?.bs || 0,
          kcl: subData[2]?.l || 0,
          kcls: subData[2]?.ls || 0,
          // K of diamond (sid 4, index 3)
          kdb: subData[3]?.b || 0,
          kdbs: subData[3]?.bs || 0,
          kdl: subData[3]?.l || 0,
          kdls: subData[3]?.ls || 0,
          // Total points (sid 5, index 4) - No is b, Yes is l
          tpb: subData[4]?.b || 0,
          tpbm: subData[4]?.bs || 0,
          tpl: subData[4]?.l || 0,
          tplm: subData[4]?.ls || 0,
          // Total cards (sid 6, index 5) - No is b, Yes is l
          tcb: subData[5]?.b || 0,
          tcbm: subData[5]?.bs || 0,
          tcl: subData[5]?.l || 0,
          tcls: subData[5]?.ls || 0,
          // Win with X (sid 7-12, index 6-11)
          w5: subData[6]?.b || 0,
          w6: subData[7]?.b || 0,
          w7: subData[8]?.b || 0,
          w15: subData[9]?.b || 0,
          w16: subData[10]?.b || 0,
          w17: subData[11]?.b || 0,
        };

        setPlayerscore(playerAScore);
      }
    }
  }, [gameData]);

  const fetchExposure = useCallback(async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = user?.user_id || user?.id;
      const matchId = gameData?.data?.data?.mid?.toString();

      if (userId && matchId) {
        const response = await getMatchExposure(userId, matchId);
        if (response.success && response.data) {
          const exposureMap = {};
          response.data.forEach((item) => {
            const teamName = item.team_name || item.selection || item.player_name || "";
            const exposureAmount = parseFloat(item.exposure_amount) || 0;
            if (teamName && exposureAmount !== 0) {
              // Create keys for the team name as-is
              const keys = createExposureKeys(teamName);
              keys.forEach(key => {
                if (key) exposureMap[key] = exposureAmount;
              });
            }
          });
          setExposures(prev => ({ ...prev, ...exposureMap }));
        }
      }
    } catch (error) {
      console.error('Error fetching exposure:', error);
    }
  }, [gameData?.data?.data?.mid]);

  useEffect(() => {
    const matchId = gameData?.data?.data?.mid?.toString();
    if (!matchId) return;

    const timeoutId = setTimeout(() => {
      fetchExposure();
    }, 5000);

    const intervalId = setInterval(() => {
      fetchExposure();
    }, 5000);

    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
  }, [gameData?.data?.data?.mid, fetchExposure]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handleBetClick = (value, selection, betData, type) => {
    // Betting functionality can be added later (admin view - no bet login)
    console.log("Bet clicked:", { value, selection, betData, type });
  };

  return (
    <Layout>
      <div className={styles.container}>
        <div className={styles.sectionA}>
          <CasinoHeading
            name="RACE 20"
            roundId={gameData?.data?.data?.mid || "157250124095658"}
            placedBetCount={myBets.length}
            activeTab={activeTab}
            onTabChange={handleTabChange}
          />

          {activeTab === "game" && (
            <>
              <VideoBox
                gameType="race20"
                resultData={gameData?.data?.data?.card}
                iframeSrc={iframeSrc}
                timerValue={gameData?.data?.data?.lt}
              />

              <div className={styles.tableContainer}>
                <div className={styles.bettable}>
                  <BetTableRace20
                    data={Playerscore}
                    runnersData={data1}
                    exposures={exposures}
                  />
                </div>
              </div>
              <div className={styles.heading}>
                <div>Last Result</div>
                <Link to="/admin/reports/casinoresult/race20">
                  <div className={styles.viewAll}>View All</div>
                </Link>
              </div>
              <ResultRace20
                data={gameData?.lrs || []}
                gameData={gameData}
              />
            </>
          )}

        </div>

        <RightSection
          sectionKey="race20"
          data={myBets}
          title="Matched"
          viewMoreText="View More"
          onViewMore={() => {
            // Handle view more action
            console.log("View more clicked for race20");
          }}
        />
      </div>
    </Layout>
  );
}

